"use client";

import React, { useMemo } from "react";
import { User } from "lucide-react";
import { UserDisplayPopover } from "../UserDisplayPopover";
import { useAdminUsers } from "@/lib/hooks/api/admin";
import { AppUser } from "@/lib/types/database";

/**
 * Minimal shape SmartUserDisplay reads from `userData`. Synthesized relation
 * rows (RelatedUser) match this shape without satisfying the full AppUser
 * interface, so we accept this narrow type at the prop boundary.
 */
export interface SmartUserDisplayData {
  id?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

interface SmartUserDisplayProps {
  userId: string;
  userData?: SmartUserDisplayData; // From relationship (if available)
  fallbackText?: string;
  showAvatar?: boolean;
  showIcon?: boolean;
  className?: string;
  disabled?: boolean; // Disable popover when editing
  showPopover?: boolean; // Show popover on hover/click (default: true). Use false in tables for expand-inline.
}

export function SmartUserDisplay({
  userId,
  userData,
  fallbackText = "Unknown",
  showAvatar = false,
  showIcon = true,
  className = "",
  disabled = false,
  showPopover = true,
}: SmartUserDisplayProps) {
  // Only fetch all users if we don't have userData and need to lookup
  const { data: allUsers, isLoading } = useAdminUsers();

  // Memoize user lookup to prevent unnecessary re-renders
  const user = useMemo(() => {
    if (userData) return userData;
    if (!allUsers || !userId) return null;
    return allUsers.find((u: AppUser) => u.id === userId);
  }, [userData, allUsers, userId]);

  // Memoize display name calculation
  const displayName = useMemo(() => {
    if (!user) return fallbackText;
    return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;
  }, [user, fallbackText]);

  // Show loading state if we're fetching and don't have data
  if (!user && isLoading && !userData) {
    return <span className={`text-gray-400 ${className}`}>Loading...</span>;
  }

  if (!user) {
    return <span className={`text-gray-400 ${className}`}>{fallbackText}</span>;
  }

  // If disabled, just show the text without popover
  if (disabled) {
    return (
      <div className={`flex items-center gap-1 rounded px-0.5 ${className}`}>
        {showIcon && <User className="h-3 w-3 text-gray-400 flex-shrink-0" />}
        <span className="text-xs text-card-foreground truncate">{displayName}</span>
      </div>
    );
  }

  // Expand inline (no popover) - e.g. in table cells
  if (!showPopover) {
    return (
      <div
        className={`flex items-center gap-2 rounded px-1 ${className}`}
      >
        {showIcon && <User className="h-4 w-4 text-gray-400" />}
        <span className="text-sm text-card-foreground">{displayName}</span>
      </div>
    );
  }

  return (
    <UserDisplayPopover user={user}>
      <div
        className={`flex items-center gap-1 cursor-pointer hover:bg-card-50 rounded px-0.5 ${className}`}
      >
        {showIcon && <User className="h-3 w-3 text-gray-400 flex-shrink-0" />}
        <span className="text-xs text-card-foreground hover:text-blue-600 truncate">{displayName}</span>
      </div>
    </UserDisplayPopover>
  );
}

interface SmartUserChipProps {
  userId: string;
  userData?: SmartUserDisplayData;
  fallbackText?: string;
  size?: "sm" | "md";
  showPopover?: boolean; // Show popover on hover/click (default: true). Use false in tables.
}

export function SmartUserChip({
  userId,
  userData,
  fallbackText = "Unknown",
  size = "sm",
  showPopover = true,
}: SmartUserChipProps) {
  const { data: allUsers, isLoading } = useAdminUsers();

  // Memoize user lookup to prevent unnecessary re-renders
  const user = useMemo(() => {
    if (userData) return userData;
    if (!allUsers || !userId) return null;
    return allUsers.find((u: AppUser) => u.id === userId);
  }, [userData, allUsers, userId]);

  // Memoize display name and initials calculation
  const { displayName, initials } = useMemo(() => {
    if (!user) return { displayName: fallbackText, initials: fallbackText.slice(0, 2) };

    const name = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;
    const userInitials = name
      .split(" ")
      .map((n: string) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

    return { displayName: name, initials: userInitials };
  }, [user, fallbackText]);

  // Show loading state if we're fetching and don't have data
  if (!user && isLoading && !userData) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-600 dark:text-gray-400"
        }
      >
        Loading...
      </span>
    );
  }

  if (!user) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-600 dark:text-gray-400"
        }
      >
        {fallbackText}
      </span>
    );
  }

  if (!showPopover) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
        }
      >
        {size === "sm" ? initials : displayName}
      </span>
    );
  }

  return (
    <UserDisplayPopover user={user}>
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:hover:bg-blue-900/50"
        }
      >
        {size === "sm" ? initials : displayName}
      </span>
    </UserDisplayPopover>
  );
}
